'use strict';

import {gsap, ScrollTrigger} from 'tc-neos-animations';

const initGsap = () => {
  gsap.registerPlugin(ScrollTrigger)

  // Detect Safari to avoid scroll issues
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  if (!isSafari) {
    // Observer only on non-Safari browsers
    const observer = new MutationObserver(() => {
      ScrollTrigger.refresh();
    });

    const container = document.querySelector('#global-container');
    if (container) {
      observer.observe(container, {childList: true, subtree: true});
    }
  }
}

const initMegaMenu = (megaMenu) => {
  gsap.set(megaMenu, {y: '-100%'})
}

const openMegaMenu = (megaMenu, onComplete = () => {
}) => {
  const isMobile = window.innerWidth <= 768

  if (isMobile) {
    gsap.set(megaMenu, {autoAlpha: 0, y: '0%'})

    gsap.to(megaMenu, {
      autoAlpha: 1,
      duration: 0.6,
      ease: 'power2.out',
      onComplete
    })
  } else {
    gsap.to(megaMenu, {
      y: '0%',
      duration: 0.8,
      ease: 'power4.inOut',
      onComplete
    })
  }
}

const closeMegaMenu = (megaMenu, onComplete = () => {
}) => {
  const isMobile = window.innerWidth <= 768

  if (isMobile) {
    gsap.to(megaMenu, {
      autoAlpha: 0,
      duration: 0.6,
      ease: 'power2.out',
      onComplete: () => {
        onComplete()
      }
    })
  } else {
    gsap.to(megaMenu, {
      y: '-100%',
      duration: 0.8,
      ease: 'power4.inOut',
      onComplete: () => {
        gsap.set(megaMenu, {y: '-100%'})
        onComplete()
      }
    })
  }
}

const animateLogo = (paths, pathsColor = 'var(--color-primary-200', duration = .7, stagger = .08) => {
  gsap.to(paths, {
    fill: pathsColor,
    duration: duration,
    stagger: stagger,
    ease: 'power2.out'
  });
}

const animateHeadings = (strongTags) => {
  // Check that the NodeList exists and is not empty
  if (!strongTags || strongTags.length === 0) return;

  strongTags.forEach((strongTag) => {
    // Check that the element exists
    if (!strongTag) return;

    strongTag.classList.remove('visible');
    strongTag.setAttribute('data-text', strongTag.textContent);

    const scrollTrigger = ScrollTrigger.create({
      trigger: strongTag,
      start: "top 80%",
      onEnter: () => {
        if (window.scrollY > 0) {
          strongTag.classList.add('visible');
        }
      }
    })

    if (scrollTrigger.isActive) {
      setTimeout(() => {
        strongTag.classList.add('visible');
      }, 1000);
    }
  })
}

const animateScrollingKeywords = (scrollingKeywords) => {
  const firstLine = scrollingKeywords.querySelector('.first-line')
  const secondLine = scrollingKeywords.querySelector('.second-line')

  // Check that elements exist before animating
  if (firstLine) {
    gsap.to(firstLine, {
      xPercent: parseInt(scrollingKeywords.dataset.firstLineHorizontalSpeed) * -1,
      ease: "none",
      scrollTrigger: {
        trigger: ".keywords-wrapper",
        start: "top bottom",
        end: "bottom+=250 top",
        scrub: true,
      }
    })
  }

  if (secondLine) {
    gsap.to(secondLine, {
      xPercent: parseInt(scrollingKeywords.dataset.secondLineHorizontalSpeed),
      ease: "none",
      scrollTrigger: {
        trigger: ".keywords-wrapper",
        start: "top bottom",
        end: "bottom+=500 top",
        scrub: true,
      }
    })
  }
}

const animateScrollingCustomers = (scrollingCustomers) => {
  // Check that the element exists
  if (!scrollingCustomers) return;

  const scrollingSpeed = scrollingCustomers.dataset.scrollingSpeed

  scrollingCustomers.querySelectorAll('.customers-row').forEach((customerRow) => {
    const logos = customerRow.querySelectorAll('.tc-website-scrollingcustomer')

    // Check that there are logos to animate
    if (logos.length === 0) return;

    gsap.set(logos, {autoAlpha: 1})
    logos.forEach((logo, i) => {
      gsap.set(logo, {xPercent: 100 * i})
    })

    const logosWrap = gsap.utils.wrap(-100, ((logos.length - 1) * 100))
    const duration = logos.length * (11 - scrollingSpeed)
    let speed = null

    customerRow.classList.contains('first-row') ? speed = `-=${logos.length * 100}` : speed = `-=${logos.length * -100}`

    gsap.to(logos, {
      xPercent: speed,
      duration,
      repeat: -1,
      ease: 'none',
      modifiers: {
        xPercent: xPercent => logosWrap(parseFloat(xPercent))
      }
    })
  })
}

const animateActiveProject = (projectsWrapper) => {
  // Check that the wrapper exists
  if (!projectsWrapper) return;

  const items = projectsWrapper.querySelectorAll('.project-item')

  // Check that there are elements to process
  if (items.length === 0) return;

  let closestItem = null
  let closestDistance = Infinity
  const viewportCenter = window.innerHeight / 2

  items.forEach(item => {
    // Check that the element exists and is in the DOM
    if (!item || !item.getBoundingClientRect) return;

    const rect = item.getBoundingClientRect()
    const itemCenter = rect.top + rect.height / 2
    const distance = Math.abs(itemCenter - viewportCenter)

    if (distance < closestDistance) {
      closestDistance = distance
      closestItem = item
    }
  })

  items.forEach(item => {
    // Check that the element exists
    if (!item) return;

    const imageWrapper = item.querySelector('.project-image img')
    const isActive = item === closestItem

    // Check that the image exists before animating
    if (imageWrapper) {
      gsap.to(imageWrapper, {
        opacity: isActive ? 0.7 : 0.3,
        duration: 0.1,
        ease: 'power2.out'
      })
    }
  })
}

const animateProjectsListContainerHeight = (container, initialHeight, newHeight) => {
  gsap.fromTo(
    container,
    {height: initialHeight},
    {
      height: newHeight,
      duration: 0.6,
      ease: "power2.out",
      onComplete: () => {
        container.style.height = ''
      }
    }
  )
}

const animateProjectsList = (listType, hiddenProjects) => {
  const tl = gsap.timeline()
  if (listType === 'grid') {
    tl.fromTo(
      hiddenProjects,
      {opacity: 0, rotationY: 90},
      {
        opacity: 1,
        rotationY: 0,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.2,
        onComplete: () => {
          hiddenProjects.forEach(project => {
            project.classList.remove('hidden');
          });
        }
      }
    )
  } else {
    tl.fromTo(
      hiddenProjects,
      {opacity: 0, y: 50},
      {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        stagger: 0.2,
        onComplete: () => {
          hiddenProjects.forEach(project => {
            project.classList.remove('hidden');
          });
        }
      }
    )
  }
}

const animateProjectContentGrid = (rows) => {
  rows.forEach((row) => {
    const tl = gsap.timeline()
    tl.fromTo(
      row.querySelectorAll('.project-content-grid-item'),
      {opacity: 0, rotationY: 90},
      {
        opacity: 1,
        rotationY: 0,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.2
      }
    )

    ScrollTrigger.create({
      trigger: row,
      start: "top bottom",
      animation: tl,
      toggleActions: "play none none none"
    })
  })
}

const animateCollaboratorsGrid = (collaborator) => {
  const front = collaborator.querySelector(".card.front")
  const back = collaborator.querySelector(".card.back")

  const flipTimeline = gsap.timeline({
    paused: true,
    defaults: {
      duration: 0.2,
      ease: "cubic-bezier(0.6, 0, 0.2, 1)"
    }
  })

  flipTimeline
    .to(front, {rotationY: 180})
    .to(back, {rotationY: 0}, "<")

  const animateMainSkill = () => {
    collaborator.querySelectorAll('.back .main-skill>span').forEach((span, i) => {
      const chars = span.querySelectorAll(".char")
      gsap.fromTo(
        chars,
        {opacity: 0},
        {opacity: 1, stagger: 0.05, delay: i * 0.2}
      )
    })
  }

  const resetMainSkill = () => {
    collaborator.querySelectorAll('.back .main-skill>span').forEach((span) => {
      span.querySelectorAll(".char").forEach((char) => {
        char.style.opacity = 0
      })
    })
  }

  const width = window.innerWidth
  collaborator.addEventListener("mouseenter", () => {
    flipTimeline.play()

    if (width > 767) {
      gsap.delayedCall(0.5, animateMainSkill)
    }
  })

  collaborator.addEventListener("mouseleave", () => {
    flipTimeline.reverse()

    if (width > 767) {
      gsap.delayedCall(0.5, resetMainSkill)
    }
  })
}

const animateBlogArticlesListContainerHeight = (container, initialHeight, newHeight) => {
  gsap.fromTo(
    container,
    {height: initialHeight},
    {
      height: newHeight,
      duration: 0.6,
      ease: "power2.out",
      onComplete: () => {
        container.style.height = ''
      }
    }
  )
}

const animateBlogArticlesList = (hiddenBlogArticles) => {
  const tl = gsap.timeline()

  tl.fromTo(
    hiddenBlogArticles,
    {opacity: 0, y: 50},
    {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: "power2.out",
      stagger: 0.2,
      onComplete: () => {
        hiddenBlogArticles.forEach(blogArticle => blogArticle.classList.remove('hidden'))
      }
    }
  )
}

const playVideoAutoplayOnScroll = (video) => {
  video.pause()

  ScrollTrigger.create({
    trigger: video,
    start: 'top 90%',
    once: false,
    onEnter: () => {
      video.play().catch(err => {
        console.warn('Erreur autoplay on enter:', err)
      })
    },
    onLeave: () => {
      video.pause()
    },
    onEnterBack: () => {
      video.play().catch(err => {
        console.warn('Erreur autoplay on enter back:', err)
      })
    },
    onLeaveBack: () => {
      video.pause()
    }
  })
}

const scrollToPosition = (scrollPosition) => {
  if (!window.lenis) return

  const start = window.scrollY
  const tween = gsap.to({value: start}, {
    value: scrollPosition,
    duration: .6,
    ease: (t) => 1 - Math.pow(1 - t, 3),
    onUpdate: function () {
      window.lenis.scrollTo(this.targets()[0].value, {immediate: true})
    }
  })

  // Cancel auto scroll if user manually scrolls
  const cancelOnUserScroll = () => {
    tween.kill() // stoppe l’animation
    window.removeEventListener('wheel', cancelOnUserScroll)
    window.removeEventListener('touchmove', cancelOnUserScroll)
  }

  // Listening for user scroll events
  window.addEventListener('wheel', cancelOnUserScroll, {passive: true})
  window.addEventListener('touchmove', cancelOnUserScroll, {passive: true})

  // Clean up event listeners when the animation completes
  tween.eventCallback('onComplete', () => {
    window.removeEventListener('wheel', cancelOnUserScroll)
    window.removeEventListener('touchmove', cancelOnUserScroll)
  })
}

export default {
  initGsap,
  initMegaMenu,
  openMegaMenu,
  closeMegaMenu,
  animateLogo,
  animateHeadings,
  animateScrollingKeywords,
  animateScrollingCustomers,
  animateActiveProject,
  animateProjectsList,
  animateProjectsListContainerHeight,
  animateProjectContentGrid,
  animateCollaboratorsGrid,
  animateBlogArticlesList,
  animateBlogArticlesListContainerHeight,
  playVideoAutoplayOnScroll,
  scrollToPosition
}
